import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react'
import { logMediaError, logToSentry } from '../helpers/sentry'

interface PermissionState {
    camera: boolean
    microphone: boolean
    checking: boolean
    devicesBusy: boolean
    devicesNotFound: boolean
    checkPermissions: () => Promise<boolean>
}

interface PermissionProviderProps {
    children: React.ReactNode
    config?: {
        checkCooldown?: number // 2000
        debounceDelay?: number // 500
        stableCheckDelay?: number // 1000
        maxRetries?: number // 3
    }
}

const PermissionContext = createContext<PermissionState | null>(null)

export function PermissionProvider({ children, config = {} }: PermissionProviderProps) {
    const { checkCooldown = 2000, debounceDelay = 500, stableCheckDelay = 1000, maxRetries = 3 } = config
    const [permissions, setPermissions] = useState({ camera: false, microphone: false })
    const [checking, setChecking] = useState(true)
    const [devicesBusy, setDevicesBusy] = useState(false)
    const [devicesNotFound, setDevicesNotFound] = useState(false)
    const checkingRef = useRef(false)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const stableCheckingRef = useRef<NodeJS.Timeout | null>(null)
    const deviceCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const lastDevicesRef = useRef<MediaDeviceInfo[]>([])
    const [retryCount, setRetryCount] = useState(0)
    const [lastCheckTime, setLastCheckTime] = useState(0)

    useEffect(() => {
        console.log('Mounting PermissionsContext')
    },[])

    const checkPermissions = useCallback(
        async (forceCheck = false) => {
            // Don't check too frequently unless forced
            const now = Date.now()
            console.log('[Permissions] checkPermissions triggered.', { forceCheck, lastCheckTime, now })
            if (!forceCheck && now - lastCheckTime < checkCooldown) {
                console.log('[Permissions] Cooldown active, skipping check.')
                return permissions.camera && permissions.microphone
            }
            setLastCheckTime(now)

            if (checkingRef.current && !forceCheck) {
                console.log('[Permissions] Already checking, skipping redundant check.')
                return permissions.camera && permissions.microphone
            }

            try {
                checkingRef.current = true
                setChecking(true)
                console.log('[Permissions] Starting permission check.')

                // Reset state values at the beginning
                let newCameraStatus = false
                let newMicrophoneStatus = false
                let isCameraBusy = false
                let isMicrophoneBusy = false
                let isCameraNotFound = false
                let isMicrophoneNotFound = false

                if (stableCheckingRef.current) {
                    clearTimeout(stableCheckingRef.current)
                }

                // First check if any devices are available
                const devices = await navigator.mediaDevices.enumerateDevices()
                lastDevicesRef.current = devices
                console.log('[Permissions] Enumerated devices:', devices)

                const hasVideoDevice = devices.some(device => device.kind === 'videoinput')
                const hasAudioDevice = devices.some(device => device.kind === 'audioinput')

                // IMPROVEMENT: Don't immediately fail if no devices found
                // Instead, attempt to request permissions anyway
                if (!hasVideoDevice && !hasAudioDevice) {
                    // Log the issue but continue with permission checks
                    // logToSentry(`Device enumeration found no devices, trying anyway`, 'device-status', {
                    //     status: 'attempting-recovery',
                    //     error: 'No devices found',
                    //     errorName: 'DeviceEnumerationEmpty',
                    // })

                    // Set temporary flags but don't block the flow
                    isCameraNotFound = !hasVideoDevice
                    isMicrophoneNotFound = !hasAudioDevice
                }

                // Try camera regardless of device enumeration results
                if (hasVideoDevice || retryCount < maxRetries) {
                    try {
                        const videoStream = await navigator.mediaDevices.getUserMedia({ video: true })
                        newCameraStatus = videoStream.getVideoTracks().length > 0
                        isCameraNotFound = false // Override if we actually got a stream
                        videoStream.getTracks().forEach(track => track.stop())
                        console.log('[Permissions] Camera check successful.')
                    } catch (error) {
                        console.info('Camera check failed:', error)
                        console.log('[Permissions] Camera getUserMedia failed:', error instanceof Error ? error.name : String(error))

                        if (error instanceof Error) {
                            if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                                isCameraBusy = true
                                // logToSentry('Camera device is busy', 'device-status', {
                                //     error: error.message,
                                //     errorName: error.name,
                                //     status: 'busy',
                                // })
                            } else if (error.name === 'NotFoundError') {
                                isCameraNotFound = true
                                // logToSentry('Camera device not found', 'device-status', {
                                //     error: error.message,
                                //     errorName: error.name,
                                //     status: 'not-found',
                                // })
                            } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                                // logToSentry('Camera permission denied', 'permissions', {
                                //     error: error.message,
                                //     errorName: error.name,
                                //     status: 'denied',
                                // })
                            }
                        }

                        logMediaError(error, 'camera permission check')
                    }
                }

                // Try microphone regardless of device enumeration results
                if (hasAudioDevice || retryCount < maxRetries) {
                    try {
                        const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true })
                        newMicrophoneStatus = audioStream.getAudioTracks().length > 0
                        isMicrophoneNotFound = false // Override if we actually got a stream
                        audioStream.getTracks().forEach(track => track.stop())
                        console.log('[Permissions] Microphone check successful.')
                    } catch (error) {
                        console.info('Microphone check failed:', error)
                        console.log('[Permissions] Microphone getUserMedia failed:', error instanceof Error ? error.name : String(error))

                        if (error instanceof Error) {
                            if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                                isMicrophoneBusy = true
                                // logToSentry('Microphone device is busy', 'device-status', {
                                //     error: error.message,
                                //     errorName: error.name,
                                //     status: 'busy',
                                // })
                            } else if (error.name === 'NotFoundError') {
                                isMicrophoneNotFound = true
                                // logToSentry('Microphone device not found', 'device-status', {
                                //     error: error.message,
                                //     errorName: error.name,
                                //     status: 'not-found',
                                // })
                            } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                                // logToSentry('Microphone permission denied', 'permissions', {
                                //     error: error.message,
                                //     errorName: error.name,
                                //     status: 'denied',
                                // })
                            }
                        }

                        logMediaError(error, 'microphone permission check')
                    }
                }

                // IMPROVEMENT: Double-check device enumeration after permission request
                // as some browsers only show devices after permissions granted
                if (!hasVideoDevice || !hasAudioDevice) {
                    const devicesAfterPermission = await navigator.mediaDevices.enumerateDevices()
                    const hasVideoDeviceNow = devicesAfterPermission.some(device => device.kind === 'videoinput')
                    const hasAudioDeviceNow = devicesAfterPermission.some(device => device.kind === 'audioinput')

                    if (hasVideoDeviceNow !== hasVideoDevice || hasAudioDeviceNow !== hasAudioDevice) {
                        // logToSentry('Devices appeared after permission request', 'device-status', {
                        //     error: `Video: ${hasVideoDevice} -> ${hasVideoDeviceNow}, Audio: ${hasAudioDevice} -> ${hasAudioDeviceNow}`,
                        //     errorName: 'DeviceEnumerationChanged',
                        //     status: 'changed',
                        // })
                        console.log('[Permissions] Device list changed after permission request.', {
                            before: { video: hasVideoDevice, audio: hasAudioDevice },
                            after: { video: hasVideoDeviceNow, audio: hasAudioDeviceNow },
                        })
                    }

                    // Update our detection state based on latest info
                    isCameraNotFound = !hasVideoDeviceNow && !newCameraStatus
                    isMicrophoneNotFound = !hasAudioDeviceNow && !newMicrophoneStatus
                }

                // Log if either permission is disabled
                if (!newCameraStatus || !newMicrophoneStatus) {
                    const disabledDevices = []
                    if (!newCameraStatus) disabledDevices.push('camera')
                    if (!newMicrophoneStatus) disabledDevices.push('microphone')
                    // logToSentry(`Media permissions disabled: ${disabledDevices.join(', ')}`, 'permissions')
                }

                // Batch update state to prevent multiple renders
                const shouldUpdatePermissions =
                    permissions.camera !== newCameraStatus || permissions.microphone !== newMicrophoneStatus

                const shouldUpdateDevicesBusy = devicesBusy !== (isCameraBusy || isMicrophoneBusy)
                const shouldUpdateDevicesNotFound = devicesNotFound !== (isCameraNotFound || isMicrophoneNotFound)

                console.log('[Permissions] Preparing to update state:', {
                    newCameraStatus,
                    newMicrophoneStatus,
                    isCameraBusy,
                    isMicrophoneBusy,
                    isCameraNotFound,
                    isMicrophoneNotFound,
                    shouldUpdatePermissions,
                    shouldUpdateDevicesBusy,
                    shouldUpdateDevicesNotFound,
                })

                if (JSON.stringify(permissions) === JSON.stringify({camera: newCameraStatus, microphone: newMicrophoneStatus})) {
                    console.log('[Permissions] No permission state changes, but updating device status flags');
                    
                    // Still set up the timeout to update all states
                    stableCheckingRef.current = setTimeout(() => {
                        console.log('[Permissions] Setting checking=false and updating device status despite no permission changes');
                        setChecking(false);
                        
                        // IMPORTANT: Still update device flags even if permissions didn't change
                        if (shouldUpdateDevicesBusy) {
                            setDevicesBusy(isCameraBusy || isMicrophoneBusy);
                        }
                        
                        if (shouldUpdateDevicesNotFound) {
                            setDevicesNotFound(isCameraNotFound || isMicrophoneNotFound);
                        }
                    }, stableCheckDelay);
                    
                    return permissions.camera && permissions.microphone;
                }

                stableCheckingRef.current = setTimeout(() => {
                    console.log('[Permissions] Applying state updates after stable check delay.')
                    setRetryCount(prev => prev + 1)

                    if (shouldUpdatePermissions) {
                        setPermissions({
                            camera: newCameraStatus,
                            microphone: newMicrophoneStatus,
                        })
                    }

                    if (shouldUpdateDevicesBusy) {
                        setDevicesBusy(isCameraBusy || isMicrophoneBusy)
                    }

                    if (shouldUpdateDevicesNotFound) {
                        setDevicesNotFound(isCameraNotFound || isMicrophoneNotFound)
                    }

                    setChecking(false)

                    // IMPROVEMENT: If the check failed but we haven't tried many times,
                    // automatically retry on a delay
                    // Updated condition to cover all busy/not-found states
                    if (
                        (isCameraNotFound || isMicrophoneBusy || isCameraBusy || isMicrophoneNotFound) &&
                        retryCount < maxRetries
                    ) {
                        setTimeout(() => {
                            console.log('[Permissions] Retrying check due to busy/not-found state.')
                            checkPermissions(true)
                        }, 2000)
                        console.log('[Permissions] Retrying check due to busy/not-found state.', {
                            retryCount,
                            maxRetries,
                            isCameraNotFound,
                            isMicrophoneBusy,
                            isCameraBusy,
                            isMicrophoneNotFound,
                        })
                    }
                }, stableCheckDelay)

                return newCameraStatus && newMicrophoneStatus
            } catch (error) {
                console.info('Permission check failed:', error)
                console.error('[Permissions] Unhandled error during permission check:', error)
                logMediaError(error, 'permission check')

                stableCheckingRef.current = setTimeout(() => {
                    setPermissions({ camera: false, microphone: false })
                    setChecking(false)
                }, 1000)

                return false
            } finally {
                checkingRef.current = false
            }
        },
        [permissions.camera, permissions.microphone, devicesBusy, devicesNotFound, retryCount, lastCheckTime, checkCooldown, stableCheckDelay, maxRetries]
    )

    // Debounced device change handler
    const debouncedDeviceCheck = useCallback(() => {
        // Clear any existing timeout to prevent multiple rapid checks
        if (deviceCheckTimeoutRef.current) {
            clearTimeout(deviceCheckTimeoutRef.current)
        }
        console.log('[Permissions] Debounced device change detected.')

        // Add a delay to handle multiple rapid device changes
        deviceCheckTimeoutRef.current = setTimeout(() => {
            console.log('[Permissions] Debounce finished, rechecking permissions due to device change...')
            checkPermissions()
        }, debounceDelay)
    }, [checkPermissions, debounceDelay])

    // Handle device changes
    useEffect(() => {
        navigator.mediaDevices.addEventListener('devicechange', debouncedDeviceCheck)

        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', debouncedDeviceCheck)
            if (deviceCheckTimeoutRef.current) {
                clearTimeout(deviceCheckTimeoutRef.current)
            }
        }
    }, [debouncedDeviceCheck])

    // Refactored main effect for setup/cleanup robustness
    useEffect(() => {
        let isMounted = true; // Flag to prevent updates after unmount
        let cameraCleanup = () => {};
        let microphoneCleanup = () => {};

        const setup = async () => {
            console.log('[Permissions] Setting up permission watchers.');
            try {
                // Check if Permissions API is supported
                if (!navigator.permissions) {
                    console.warn('[Permissions] navigator.permissions API not supported. Cannot watch for permission changes automatically.');
                    // Perform initial check regardless, as getUserMedia might still work
                    if (isMounted) { // Check flag before initial check
                        console.log('[Permissions] Performing initial check (no Permissions API).');
                        checkPermissions();
                    }
                    return; // Exit setup if API not available
                }

                // Query initial status
                const camStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
                const micStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });

                // Centralized handler for permission changes
                const handlePermissionChange = (event: Event) => {
                    const target = event.target as PermissionStatus;
                    const permissionName = target.name; // 'camera' or 'microphone'
                    console.log(`[Permissions] Permission status changed for ${permissionName}. New state: ${target.state}`);

                    if (!isMounted) { // Check flag before debouncing
                        console.log(`[Permissions] Component unmounted, ignoring ${permissionName} change.`);
                        return;
                    }

                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }
                    console.log(`[Permissions] Debouncing permission check due to ${permissionName} change.`);
                    setChecking(true); // Optimistic checking state

                    timeoutRef.current = setTimeout(() => {
                        if (!isMounted) { // Check flag again before checkPermissions
                            console.log(`[Permissions] Debounce finished, but component unmounted. Skipping check.`);
                            return;
                        }
                        console.log(`[Permissions] Debounce finished, rechecking permissions due to ${permissionName} status change.`);
                        checkPermissions(); // Consider forceCheck=true if needed
                    }, debounceDelay);
                };

                // Add listeners
                camStatus.addEventListener('change', handlePermissionChange);
                micStatus.addEventListener('change', handlePermissionChange);

                // Define synchronous cleanup functions
                cameraCleanup = () => {
                    console.log('[Permissions] Cleaning up camera listener.');
                    camStatus.removeEventListener('change', handlePermissionChange);
                };
                microphoneCleanup = () => {
                    console.log('[Permissions] Cleaning up microphone listener.');
                    micStatus.removeEventListener('change', handlePermissionChange);
                };

                // Log initial denied state
                if (camStatus.state === 'denied') {
                    console.log('[Permissions] Initial camera permission state: denied');
                    // logToSentry(`camera permission denied`, 'permissions');
                }
                if (micStatus.state === 'denied') {
                    console.log('[Permissions] Initial microphone permission state: denied');
                    // logToSentry(`microphone permission denied`, 'permissions');
                }

                // Perform initial check only after setup is complete and component is still mounted
                if (isMounted) { // Check flag before initial check
                   console.log('[Permissions] Permission watchers set up. Performing initial check.');
                   checkPermissions();
                }
            } catch (error) {
                 if (isMounted) { // Check flag before logging/checking
                    console.error(`Error setting up permission watchers:`, error);
                    // logToSentry(
                    //     `Failed to watch permissions: ${error instanceof Error ? error.message : String(error)}`,
                    //     'permissions'
                    // );
                    // Optionally perform an initial check even if watchers failed
                    console.log('[Permissions] Performing initial check despite watcher setup error.');
                    checkPermissions();
                 }
            }
        };

        setup(); // Execute the async setup function

        // Cleanup function
        return () => {
            isMounted = false; // Set flag on cleanup
            console.log('[Permissions] Cleaning up PermissionsProvider effect.');
            if (timeoutRef.current) {
                console.log('[Permissions] Clearing permission change timeout.');
                clearTimeout(timeoutRef.current);
            }
            if (stableCheckingRef.current) {
                console.log('[Permissions] Clearing stable check timeout.');
                clearTimeout(stableCheckingRef.current);
            }
            // Synchronous cleanup calls
            cameraCleanup();
            microphoneCleanup();
        };
    }, [debounceDelay]);

    return (
        <PermissionContext.Provider
            value={{
                ...permissions,
                checking,
                devicesBusy,
                devicesNotFound,
                checkPermissions,
            }}
        >
            {children}
        </PermissionContext.Provider>
    )
}

export const usePermissions = () => {
    const context = useContext(PermissionContext)
    if (!context) {
        throw new Error('usePermissions must be used within a PermissionProvider')
    }
    return context
}
